import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { User } from '../../../models/index';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { Constants } from 'src/app/app.constants';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';

interface RecipientType {
  value: string;
  label: string;
}

import {
  AuthService,
  MerchantService,
  RewardService,
} from '../../../services/index';
import { Customer, Reward } from '../../../models/index';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';

@Component({
  selector: 'give-reward-dialog',
  templateUrl: './give-reward-dialog.component.html',
  styleUrls: ['./give-reward-dialog.component.css'],
})
export class GiveRewardDialogComponent implements OnInit {
  form!: FormGroup;
  rewardFormControl!: FormControl;
  customerFormControl!: FormControl;
  recipientTypeFormControl!: FormControl;
  emailFormControl!: FormControl;
  numberFormControl!: FormControl;

  saving = false;
  customers: Customer[] = [];
  rewards: Reward[] = [];
  rewardsLoading = false;
  customersLoading = false;
  error = false;

  notify = false;
  sendEmail = true;

  merchantActive = true;

  recipientTypeOptions: RecipientType[] = [
    { value: 'customer', label: 'Existing Customer' },
    { value: 'email', label: 'Email Address' },
  ];

  get loading(): boolean {
    return this.customersLoading || this.rewardsLoading;
  }

  get isSpecificCustomer(): boolean {
    return this.recipientTypeFormControl?.value?.value === 'customer';
  }

  get isEmailRecipient(): boolean {
    return this.recipientTypeFormControl?.value?.value === 'email';
  }

  quantityOptions = Array.from({ length: 10 }, (_, index) => index + 1)
    .filter((el: number) => {
      return !this.rewards.some((ex: any) => ex?.stamp?.number === el);
    })
    .map((el: number) => ({ number: el }));

  constructor(
    public dialog: MatDialog,
    public matDialogRef: MatDialogRef<GiveRewardDialogComponent>,
    public fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private rewardService: RewardService,
    private constants: Constants,
    private merchantService: MerchantService,
    private mixpanel: AnalyticsAbstract,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.merchantActive = this.authService.merchantObj?.active ?? true;

    this.rewardFormControl = new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]);
    this.customerFormControl = new FormControl(
      { value: '', disabled: false },
      [],
    );
    this.recipientTypeFormControl = new FormControl(
      this.recipientTypeOptions[0],
      [Validators.required],
    );
    this.emailFormControl = new FormControl({ value: '', disabled: false }, []);
    this.numberFormControl = new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]);

    this.form = this.fb.group({
      reward: this.rewardFormControl,
      number: this.numberFormControl,
      customer: this.customerFormControl,
      recipientType: this.recipientTypeFormControl,
      email: this.emailFormControl,
    });

    // Set up validation changes based on recipient type
    this.recipientTypeFormControl.valueChanges.subscribe((type) => {
      this.updateValidators(type);
    });

    this.getRewards();

    if (!this.data?.customer) {
      this.getCustomers();
      this.updateValidators(this.recipientTypeFormControl.value);
    }
  }

  updateValidators(recipientType: RecipientType): void {
    // Clear all validators first
    this.customerFormControl.clearValidators();
    this.emailFormControl.clearValidators();

    if (recipientType?.value === 'customer') {
      this.customerFormControl.setValidators([Validators.required]);
    } else if (recipientType?.value === 'email') {
      this.emailFormControl.setValidators([
        Validators.required,
        Validators.email,
      ]);
    }

    // Update validity
    this.customerFormControl.updateValueAndValidity();
    this.emailFormControl.updateValueAndValidity();
  }

  giveReward(): void {
    const quantity = this.numberFormControl.value.number;
    const reward = this.rewardFormControl.value;

    this.saving = true;

    if (this.data?.customer || this.isSpecificCustomer) {
      // Give reward to existing customer
      const customer =
        this.data?.customer?._id ?? this.customerFormControl?.value?._id;

      this.merchantService
        .giveReward(customer, reward._id, quantity, this.notify)
        .subscribe({
          next: (res: any) => {
            if (res) {
              this.giveRewardConfirmation(quantity, reward.title);
            }
          },
          error: (res: HttpErrorResponse) => {
            this.error = true;
            this.constants.snack(res.error.message);
          },
        })
        .add(() => (this.saving = false));
    } else if (this.isEmailRecipient) {
      // Gift reward by email
      const email = this.emailFormControl.value;

      this.merchantService
        .giftRewardByEmail(reward._id, quantity, email, this.sendEmail)
        .subscribe({
          next: (res: any) => {
            if (res) {
              this.giveRewardConfirmation(
                quantity,
                reward.title,
                email,
                res.userExists,
              );
            }
          },
          error: (res: HttpErrorResponse) => {
            this.error = true;
            this.constants.snack(res.error.message);
          },
        })
        .add(() => (this.saving = false));
    }
  }

  giveRewardConfirmation(
    quantity: number,
    reward: string,
    email?: string,
    userExists?: boolean,
  ): void {
    let message: string;

    if (email) {
      // Email recipient
      message = userExists
        ? `You have given ${email} ${quantity} <b>${reward}</b> ${
            quantity === 1 ? 'reward' : 'rewards'
          }, which will now show in their Rewards Wallet`
        : `You have gifted ${quantity} <b>${reward}</b> ${
            quantity === 1 ? 'reward' : 'rewards'
          } to ${email}. ${
            this.sendEmail
              ? 'They will receive an email invitation to join Remy Rewards and claim their reward(s).'
              : 'The reward(s) will be available when they sign up to Remy Rewards with this email address.'
          }`;
    } else {
      // Existing customer
      message = `You have given ${
        this.customerName
      } ${quantity} <b>${reward}</b> ${
        quantity === 1 ? 'reward' : 'rewards'
      }, which will now show in their Rewards Wallet`;
    }

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirmed',
        body: message,
        buttonTwo: 'OK',
      },
      autoFocus: false,
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe(() => window.location.reload());
  }

  get customerName(): string {
    return (
      this.data?.customer?.user?.name ?? this.customerFormControl?.value?.name
    );
  }

  getCustomers(): void {
    this.customersLoading = true;
    this.merchantService
      .getCustomers('_id', 'desc', 2000, 0, '', {})
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.customers = res.docs;

            this.customers.forEach((el) => {
              if (typeof el.user !== 'string') {
                el.name = el.user?.name ?? el?.name;
                el.email = el.user?.email ?? el?.email;
              }
            });

            this.customers.sort((a, b) => a.name!.localeCompare(b.name!));
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.customersLoading = false));
  }

  getRewards(): void {
    this.rewardsLoading = true;
    this.rewardService
      .getRewards(1000, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.rewards = res.docs;
            this.rewards?.sort((a, b) => a.title!.localeCompare(b.title!));
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.rewardsLoading = false));
  }

  primaryTapped() {
    if (this.form?.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const quantity = this.numberFormControl.value.number;
    const reward = this.rewardFormControl.value;

    let confirmMessage: string = '';

    if (this.data?.customer || this.isSpecificCustomer) {
      // Existing customer confirmation
      confirmMessage = `Do you want to give ${
        this.customerName
      } ${quantity} <b>${reward.title}</b> ${
        quantity === 1 ? 'reward' : 'rewards'
      }? <br><br>${
        quantity === 1 ? 'This' : 'These'
      } will show in their rewards wallet and they <b>will</b> ${
        this.notify ? '' : '<b>not</b>'
      } be notified`;
    } else if (this.isEmailRecipient) {
      // Email recipient confirmation
      const email = this.emailFormControl.value;
      confirmMessage = `Do you want to gift ${quantity} <b>${
        reward.title
      }</b> ${quantity === 1 ? 'reward' : 'rewards'} to ${email}? <br><br>${
        quantity === 1 ? 'This' : 'These'
      } will be available when they sign up to Remy Rewards and they <b>will</b> ${
        this.sendEmail ? '' : '<b>not</b>'
      } be notified by email`;
    }

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm',
        body: confirmMessage,
        buttonOne: 'Cancel',
        buttonTwo: 'Yes',
      },
      autoFocus: false,
      width: '300px',
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((option) => {
      if (option == 1) {
        this.giveReward();
        this.mixpanel.track(Constants.analytics_keys.giveReward, {
          Customer: this.customerName,
          Reward: reward.title,
          Quantity: quantity,
        });
      }
    });
  }

  hasRewards(): void {
    if (!this.rewards.length) {
      this.constants.snack('Please add a reward first');
    }
  }
}
